import { Divider, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import { filter, isEmpty, map } from 'lodash';
import React, { Fragment, useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import {
  FormControls,
  FormikArray,
  FormikCheckbox,
  FormikNumericField,
  FormikSelect,
} from 'components';
import { useCrossFiatCurrencyExchange } from 'hooks';
import { TranslationNamespace } from 'i18n';

type FormValues = {
  payinFiatCurrencyExchangeConfigs: {
    fiatCurrencyExchangeId: string;
    minSpread?: number | null;
    maxSpread?: number | null;
    traderSpreadCompensation?: boolean;
  }[];
};

type Props = {
  fiatCurrencyId: string;
  hideTitle?: boolean;
  onChange?: () => void;
};

export const ShopPayinFiatCurrencyExchangeConfigs: React.FC<Props> = ({
  fiatCurrencyId,
  hideTitle,
  onChange,
}) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.shops.manage',
  });
  const { t: tCommon } = useTranslation();
  const formik = useFormikContext<FormValues>();

  const { crossFiatCurrencyExchangeOptions } = useCrossFiatCurrencyExchange({
    enabled: true,
    fiatCurrencyId,
  });

  const getFiatCurrencyExchangeOptions = useCallback(
    (exclude: string[]) =>
      filter(
        crossFiatCurrencyExchangeOptions,
        (fiatCurrencyExchangeOption) =>
          !exclude?.includes(fiatCurrencyExchangeOption.value),
      ),
    [crossFiatCurrencyExchangeOptions],
  );

  const renderItemComponent = useCallback(
    ({ index, item }: { index: number; item: any }) => {
      const exclude = map(
        filter(
          formik.values.payinFiatCurrencyExchangeConfigs,
          (c) => c.fiatCurrencyExchangeId !== item.fiatCurrencyExchangeId,
        ),
        (c) => c.fiatCurrencyExchangeId,
      );
      return (
        <FormikSelect
          label={t('fields.payin_fiat_currency_exchange')}
          name={`payinFiatCurrencyExchangeConfigs[${index}].fiatCurrencyExchangeId`}
          options={getFiatCurrencyExchangeOptions(exclude)}
          noneOption
          disabled={isEmpty(crossFiatCurrencyExchangeOptions)}
          sx={{ mr: 1, flex: 1 }}
        />
      );
    },
    [
      t,
      crossFiatCurrencyExchangeOptions,
      getFiatCurrencyExchangeOptions,
      formik.values.payinFiatCurrencyExchangeConfigs,
    ],
  );

  const renderAdditionalItemContent = useCallback(
    ({ index }: { index: number }) => (
      <Fragment>
        <Divider>{t('fields.payin_fiat_currency_exchange_spread')}</Divider>
        <FormControls row>
          <FormikNumericField
            label={t('fields.min')}
            name={`payinFiatCurrencyExchangeConfigs[${index}].minSpread`}
            defaultValue={0}
            fullWidth
            percentageSuffix
          />
          <FormikNumericField
            label={t('fields.max')}
            name={`payinFiatCurrencyExchangeConfigs[${index}].maxSpread`}
            defaultValue={0}
            fullWidth
            percentageSuffix
          />
        </FormControls>
        <FormikCheckbox
          name={`payinFiatCurrencyExchangeConfigs[${index}].traderSpreadCompensation`}
          label={t('fields.payin_trader_spread_compensation')}
        />
      </Fragment>
    ),
    [t],
  );

  return (
    <Fragment>
      {!hideTitle && (
        <Typography variant="subtitle1">
          {`${t('sections.cross_currency_exchanges.title')} (${tCommon(
            'common.payin',
          )})`}
        </Typography>
      )}

      <FormikArray
        addItemLabel={t('buttons.add_сross_currency_exchange')}
        name="payinFiatCurrencyExchangeConfigs"
        removable
        ItemComponent={renderItemComponent}
        AdditionalItemContent={renderAdditionalItemContent}
        defaultValue={{
          fiatCurrencyExchangeId: '',
          minSpread: 0,
          maxSpread: 0,
          traderSpreadCompensation: false,
        }}
        onChange={onChange}
      />
    </Fragment>
  );
};
